// Per-kind FIELD configuration for the universal entity create/edit modal.
// Pure data + types only — no React, no side effects. Consumed by entity-form.tsx
// (which re-exports EntityKind) and entity-form-payload.ts.

export type EntityKind = "task" | "project" | "goal" | "habit" | "note" | "transaction" | "budget" | "event" | "recurring" | "account";

export type Field = {
  name: string;
  label: string;
  type: "text" | "textarea" | "markdown" | "date" | "time" | "number" | "duration" | "money" | "select" | "color" | "checkbox" | "category";
  options?: Array<{ value: string; label: string }>;
  required?: boolean;
  span?: boolean; // full width
  // Ràng buộc cho type "number" — vi phạm sẽ CHẶN nút Tạo/Lưu kèm thông báo
  // (không kẹp âm thầm như trước; sanitize layer vẫn clamp làm lưới an toàn).
  min?: number;
  max?: number;
};

export type FieldConfig = { title: string; fields: Field[] };

/** Build a select option list from [value, label] pairs. */
export const opt = (pairs: Array<[string, string]>) => pairs.map(([value, label]) => ({ value, label }));

export const CONFIG: Record<EntityKind, FieldConfig> = {
  task: {
    title: "việc cần làm",
    fields: [
      { name: "title", label: "Tiêu đề việc", type: "text", required: true, span: true },
      { name: "description", label: "Mô tả", type: "textarea", span: true },
      { name: "dueDate", label: "Hạn", type: "date" },
      { name: "deferDate", label: "Bắt đầu (ẩn tới ngày)", type: "date" },
      { name: "priority", label: "Ưu tiên", type: "select", options: opt([["low", "Thấp"], ["medium", "Trung bình"], ["high", "Cao"]]) },
      { name: "estimateMinutes", label: "Thời lượng", type: "duration", min: 0, max: 1440 },
      { name: "status", label: "Trạng thái", type: "select", options: opt([["todo", "Chưa làm"], ["doing", "Đang làm"], ["done", "Xong"]]) },
      { name: "tags", label: "Thẻ (cách nhau dấu phẩy)", type: "text", span: true },
    ],
  },
  project: {
    title: "dự án",
    fields: [
      { name: "name", label: "Tên dự án", type: "text", required: true, span: true },
      { name: "area", label: "Lĩnh vực", type: "text" },
      { name: "deadline", label: "Hạn", type: "date" },
      { name: "progress", label: "Tiến độ (%)", type: "number", min: 0, max: 100 },
      { name: "status", label: "Trạng thái", type: "select", options: opt([["active", "Đang chạy"], ["paused", "Tạm dừng"], ["done", "Xong"]]) },
      { name: "nextAction", label: "Bước tiếp theo", type: "text", span: true },
    ],
  },
  goal: {
    title: "mục tiêu",
    fields: [
      { name: "title", label: "Tên mục tiêu", type: "text", required: true, span: true },
      { name: "objective", label: "Mục đích (vì sao)", type: "textarea", span: true },
      { name: "horizon", label: "Khoảng", type: "select", options: opt([["quarter", "Quý"], ["year", "Năm"], ["life", "Đời"]]) },
      { name: "metric", label: "Chỉ số", type: "text" },
      { name: "unit", label: "Đơn vị", type: "text" },
      { name: "current", label: "Hiện tại", type: "number" },
      { name: "target", label: "Mục tiêu", type: "number" },
      { name: "deadline", label: "Hạn", type: "date" },
    ],
  },
  habit: {
    title: "thói quen",
    fields: [
      { name: "name", label: "Tên thói quen", type: "text", required: true, span: true },
      { name: "targetPerWeek", label: "Mục tiêu / tuần", type: "number" },
      { name: "color", label: "Màu", type: "color" },
      // linkedGoalId injected as dynamic select in entity-form.tsx (D14)
    ],
  },
  note: {
    title: "ghi chú",
    fields: [
      { name: "title", label: "Tiêu đề", type: "text", required: true, span: true },
      { name: "tags", label: "Thẻ (cách nhau bởi dấu phẩy)", type: "text", span: true },
      { name: "body", label: "Nội dung", type: "markdown", required: true, span: true },
      { name: "pinned", label: "Ghim", type: "checkbox" },
      // category + linkedProjectId injected as dynamic selects in entity-form.tsx (D16/PARA)
    ],
  },
  transaction: {
    title: "giao dịch",
    fields: [
      { name: "type", label: "Loại", type: "select", options: opt([["expense", "Chi tiêu"], ["income", "Thu nhập"]]) },
      { name: "amount", label: "Số tiền", type: "money", required: true },
      // "category": dropdown từ danh mục ngân sách + danh mục đã dùng (entity-form wire options)
      { name: "category", label: "Danh mục", type: "category", required: true },
      { name: "date", label: "Ngày", type: "date" },
      { name: "note", label: "Ghi chú", type: "text", span: true },
    ],
  },
  budget: {
    title: "ngân sách",
    fields: [
      // D43: ngân sách cũng chọn từ danh sách danh mục chi tiêu được quản trị
      { name: "category", label: "Danh mục", type: "category", required: true, span: true },
      { name: "monthlyLimit", label: "Hạn mức / tháng", type: "money", required: true },
      // D49: cảnh báo khi đã chi đạt % này (chưa vượt hẳn) — nhắc trước khi lố.
      { name: "alertThreshold", label: "Cảnh báo khi dùng (%)", type: "number", min: 1, max: 100 },
      { name: "rollover", label: "Cộng dồn tháng sau (rollover)", type: "checkbox" },
    ],
  },
  // Event: base fields only; endTime/durationMin/rrule handled separately below form fields.
  event: {
    title: "lịch",
    fields: [
      { name: "title", label: "Tiêu đề", type: "text", required: true, span: true },
      { name: "date", label: "Ngày", type: "date" },
      { name: "time", label: "Giờ bắt đầu", type: "time" },
      { name: "endTime", label: "Giờ kết thúc", type: "time" },
      { name: "durationMin", label: "Thời lượng (phút)", type: "number" },
      { name: "color", label: "Màu", type: "color" },
    ],
  },
  recurring: {
    title: "khoản định kỳ",
    fields: [
      { name: "type", label: "Loại", type: "select", options: opt([["income", "Thu nhập (vd: lương)"], ["expense", "Chi cố định"]]) },
      { name: "category", label: "Tên khoản", type: "text", required: true, span: true },
      { name: "amount", label: "Số tiền", type: "money", required: true },
      // D49: tần suất. Weekly bỏ qua "ngày trong tháng" (dùng cadence từ ngày bắt đầu).
      { name: "frequency", label: "Tần suất", type: "select", options: opt([["monthly", "Hằng tháng"], ["weekly", "Hằng tuần"], ["quarterly", "Hằng quý"], ["yearly", "Hằng năm"]]) },
      { name: "dayOfMonth", label: "Ngày trong tháng (1–28)", type: "number", min: 1, max: 28 },
      { name: "endOfMonth", label: "Dùng ngày cuối tháng (bỏ qua ô trên)", type: "checkbox" },
      // D51: trống = chạy mãi; đặt ngày để dừng (trả góp / lương có thời hạn).
      { name: "endDate", label: "Kết thúc (trống = chạy mãi)", type: "date" },
      { name: "note", label: "Ghi chú", type: "text", span: true },
    ],
  },
  // D4/D38: Account/Ví — name + type + openingBalance + currency (D38 multi-currency).
  account: {
    title: "ví / tài khoản",
    fields: [
      { name: "name", label: "Tên ví", type: "text", required: true, span: true },
      { name: "type", label: "Loại ví", type: "select", options: opt([["cash", "Tiền mặt"], ["bank", "Ngân hàng"], ["ewallet", "Ví điện tử"], ["credit", "Thẻ tín dụng"], ["other", "Khác"]]) },
      { name: "openingBalance", label: "Số dư ban đầu", type: "number" },
      { name: "currency", label: "Tiền tệ", type: "select", options: opt([["VND", "VND — Đồng"], ["USD", "USD — Đô la Mỹ"], ["EUR", "EUR — Euro"], ["JPY", "JPY — Yên Nhật"], ["GBP", "GBP — Bảng Anh"], ["SGD", "SGD — Đô la Singapore"], ["THB", "THB — Baht Thái"], ["CNY", "CNY — Nhân dân tệ"], ["KRW", "KRW — Won Hàn Quốc"], ["AUD", "AUD — Đô la Úc"]]) },
    ],
  },
};
